import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, ArrowRight, ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("details");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "", industry: "", company_size: "", address: "", admin_name: "", admin_email: "", password: "",
  });
  const [code, setCode] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [legal, setLegal] = useState(null);
  const [legalView, setLegalView] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/legal").then((r) => setLegal(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error("Please accept the Terms & Conditions and Privacy Policy."); return; }
    setBusy(true);
    try {
      await api.post("/auth/signup-company", { ...form, accepted_terms: accepted });
      toast.success("Company created! Enter the code we emailed you.");
      setStep("verify");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: form.admin_email, code });
      await login(data.token, data.user, data.company);
      toast.success("You're all set!");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0D0C] grid-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center"><Clock className="w-5 h-5 text-[#062217]" /></div>
          <span className="font-display text-2xl font-extrabold tracking-tight">QwikLogs</span>
        </Link>

        <div className="card-emerald p-8 animate-fade-up">
          {step === "details" ? (
            <>
              <h1 className="font-display text-2xl font-bold">Create your company</h1>
              <p className="text-sm text-muted-foreground mt-1">Start your 14-day trial for just $0.99 — pay securely after sign-up.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="company_name">Company name</Label>
                  <Input id="company_name" required value={form.company_name} onChange={set("company_name")}
                    className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-company-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="industry">Industry</Label>
                    <Input id="industry" value={form.industry} onChange={set("industry")}
                      className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-industry-input" />
                  </div>
                  <div>
                    <Label>Company size</Label>
                    <Select value={form.company_size} onValueChange={(v) => setForm({ ...form, company_size: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-size-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-[#121816] border-emerald-500/20">
                        {["1-10", "11-50", "51-200", "200+"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Textarea id="address" value={form.address} onChange={set("address")} rows={2}
                    className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-address-input" />
                </div>
                <div className="h-px bg-emerald-500/15 my-2" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="admin_name">Your name</Label>
                    <Input id="admin_name" required value={form.admin_name} onChange={set("admin_name")}
                      className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-admin-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="admin_email">Your email</Label>
                    <Input id="admin_email" type="email" required value={form.admin_email} onChange={set("admin_email")}
                      className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-admin-email-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")}
                    placeholder="At least 6 characters — for email + password sign-in"
                    className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="signup-password-input" />
                </div>
                <div className="flex items-start gap-2.5 pt-1" data-testid="terms-block">
                  <input type="checkbox" id="accept-terms" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 w-4 h-4 accent-emerald-500 shrink-0" data-testid="accept-terms-checkbox" />
                  <label htmlFor="accept-terms" className="text-xs text-muted-foreground leading-relaxed">
                    I have read and agree to QwikLogs Africa's{" "}
                    <button type="button" className="text-emerald-400 hover:underline" onClick={() => setLegalView("terms")} data-testid="view-terms-link">Terms &amp; Conditions</button>{" "}
                    and{" "}
                    <button type="button" className="text-emerald-400 hover:underline" onClick={() => setLegalView("privacy")} data-testid="view-privacy-link">Privacy Policy</button>
                    {legal?.terms?.updated_at ? ` (updated ${new Date(legal.terms.updated_at).toLocaleDateString()}).` : "."}
                  </label>
                </div>
                <Button type="submit" disabled={busy || !accepted} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="signup-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create company <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </form>
            </>
          ) : (
            <>
              <button onClick={() => setStep("details")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-2 text-emerald-300 text-sm mb-2"><MailCheck className="w-4 h-4" /> Verify your email</div>
              <h1 className="font-display text-2xl font-bold">Enter your code</h1>
              <p className="text-sm text-muted-foreground mt-1">We sent a 6-digit code to {form.admin_email}.</p>
              <form onSubmit={verify} className="mt-6 space-y-4">
                <Input inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="000000" maxLength={6}
                  className="bg-[#0A0D0C] border-emerald-500/20 font-mono text-center text-2xl tracking-[0.4em]" data-testid="signup-code-input" />
                <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="signup-verify-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & continue"}
                </Button>
              </form>
            </>
          )}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-6">
          Already have an account? <Link to="/login" className="text-emerald-400 hover:underline">Sign in</Link>
        </p>
      </div>

      <Dialog open={!!legalView} onOpenChange={(o) => !o && setLegalView(null)}>
        <DialogContent className="bg-[#0C110F] border-emerald-500/20 max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="legal-dialog">
          <DialogHeader><DialogTitle>{legal?.[legalView]?.title || "Legal"}</DialogTitle></DialogHeader>
          {legal?.[legalView] ? (
            <>
              <p className="text-xs text-muted-foreground">Version {legal[legalView].version} · Updated {new Date(legal[legalView].updated_at).toLocaleDateString()}</p>
              <div className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed mt-2">{legal[legalView].content}</div>
            </>
          ) : <p className="text-sm text-muted-foreground">Loading…</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
